<template>
    <div class="main__assessment__box__wrapper">
        <!-- Mobile menu toggle -->
        <button class="mobile-menu-btn" @click="toggleSidebar">
            <img :src="hamburgerIcon" />
        </button>
        <div class="group__box">
            <!-- Sidebar -->
            <aside class="assessment__sidebar__box" :class="{ 'sidebar-open': showSidebar }">
                <div class="info rlms-flex rlms-items-center rlms-justify-between rlms-w-100">
                    <div class="rlms-flex rlms-gap-10 rlms-items-center">
                        <img :src="profileIcon" />
                        <h3 class="title" id="assessmentNo">{{ assessment?.learner?.firstname }} {{
                            assessment?.learner?.lastname }}</h3>
                    </div>
                    <div class="purple__solid__btn" title="Back">
                        <a href="javascript:void(0)" @click.prevent="router.back();">
                            <span class="btn__icon">
                                <img :src="purpleBtnArrowLeft" />
                            </span>
                        </a>
                    </div>
                </div>
                <div class="assessment__sidebar__box_menu">
                    <h3 class="title" id="assessmentNo">Assessment #{{ assessment?.serialno }}</h3>
                    <div class="divider__sepline"></div>

                    <!-- Tab Navigation -->
                    <div class="tab__menu__item__box">
                        <ul id="tabs-nav">
                            <li v-for="group in assessmentGroups" :key="group.id"
                                :class="{ active: activeTab === group.tab, 'red__flag': flaggedCounts?.[group.id] > 0 }"
                                @click="switchTab(group)">
                                <a href="javascript:void(0)">{{ group.name }}</a>
                            </li>
                        </ul>

                        <div class="divider__sepline"></div>

                        <!-- Work Packet Button -->
                        <div class="tab__btn__box">
                            <RouterLink
                                :to="`/organizations/${organizationId}/learners/${learnerId}/workpacket/${assessmentId}/guided`">
                                <span class="btn__icon">
                                    <img :src="globalIcon" />
                                </span>
                                <span class="btn__text">Work Packet</span>
                            </RouterLink>
                        </div>

                        <!-- Export JSON Button -->
                        <div class="tab__btn__box">
                            <a href="javascript:void(0);" @click="exportAssessment">
                                <span class="btn__icon">
                                    <img :src="downloadIcon" />
                                </span>
                                <span class="btn__text">Export JSON</span>
                            </a>
                        </div>
                    </div>

                    <!-- Assessment Dropdown and Back -->
                    <div class="tab__btn__group">
                        <div class="purple__trans__btn">
                            <select class="select__btn" @change="handleAssessmentChange">
                                <option v-for="(assessment, index) in learnerAssessments" :key="assessment.id"
                                    :value="assessment.id" :selected="assessmentId === assessment.id">
                                    Assessment #{{ learnerAssessments?.length - index }}
                                </option>
                            </select>
                        </div>

                        <div class="purple__solid__btn">
                            <a href="javascript:void(0)" @click="goToLearnerDashboard">
                                <span class="btn__icon">
                                    <img :src="purpleBtnArrowLeft" />
                                </span>
                                <span class="btn__text">Back To Learner Dashboard</span>
                            </a>
                        </div>
                    </div>
                </div>
            </aside>
            <div class="sidebar-overlay" v-if="showSidebar" @click="showSidebar = false">
            </div>

            <!-- Assessment Content Section -->
            <section class="assetssment__main__data__box assessment__main__data__box__outer">
                <template v-for="group in assessmentGroups" :key="group.tab">
                    <div class="assetssment__cover__box tab-content" v-if="activeTab === group.tab && group.id !== 6">
                        <AssessmentGroup :groupId="group.id" :organizationId="organizationId"
                            :assessmentId="assessmentId" :assessment="assessment"
                            :agreement="organization?.agreement"
                            v-if="activeTab === group.tab" @next-group="goToNextGroup(group.id)"
                            @prev-group="goToPrevGroup(group.id)" @updated="async () => { await fetchAssessment() }"
                            @update-flagged-count="({ groupId, count }) => {
                                flaggedCounts = {
                                    ...flaggedCounts,
                                    [groupId]: count
                                };
                            }" @update-zero-count="({ groupId, count, lastUpdatedAttemptId }) => {
                                zeroScoreCounts = {
                                    ...zeroScoreCounts,
                                    [groupId]: count
                                };

                                lastAttemptId = lastUpdatedAttemptId;                                
                            }" :canConductAssessment="canConductAssessment" />
                    </div>
                    <AssessmentResult v-if="activeTab === group.tab && group.id === 6" :assessment="assessment" />
                </template>
            </section>
            <EasilyModal v-if="showEasilyModel && canConductAssessment" @saved="skipGroup()"
                @close="showEasilyModel = false;" />
            <AutomateScoringModal v-if="showAutomateScoringModal" @saved="async () => { await fetchAssessment() }"
                @close="showAutomateScoringModal = false;" :organizationId="organizationId" :assessmentId="assessmentId"
                :group="currentGroup.id" :lastAttemptId="lastAttemptId" />
        </div>
    </div>
</template>

<script setup>
import AssessmentGroup from '@/components/assesments/AssessmentGroup.vue';
import AssessmentResult from '@/components/assesments/AssessmentResult.vue';
import AutomateScoringModal from '@/components/assesments/AutomateScoringModal.vue';
import EasilyModal from '@/components/assesments/EasilyModal.vue';
import api from '@/services/axios';
import { useLoaderStore } from '@/stores/loader';
import { assessmentGroups } from '@/utils/assessmentGroups';
import { zeroRunsByGroup, CEILING_GROUPS, CEILING_RUN_LENGTH } from '@/utils/assessment-ceiling';
import { handleError } from '@/utils/handleError';
import { ref, onMounted, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import globalIcon from '@/assets/images/global-icon.svg'
import purpleBtnArrowLeft from '@/assets/images/purple-btn-arrow-left.svg'
import hamburgerIcon from '@/assets/images/hamburger.png'
import profileIcon from '@/assets/images/profile.svg'
import downloadIcon from '@/assets/images/arrow-down.svg'

const router = useRouter();
const route = useRoute();
const organizationId = ref(Number(route.params.organization ?? 0));
const learnerId = ref(Number(route.params.learner ?? 0));
const assessmentId = ref(Number(route.params.assessment ?? 0));
const flaggedCounts = ref({});
const zeroScoreCounts = ref({});
const lastAttemptId = ref(null);
const showSidebar = ref(false);
const organization = ref(null);

const toggleSidebar = () => {
    showSidebar.value = !showSidebar.value;
};

const loader = useLoaderStore();

// State
const assessment = ref(null)
const activeTab = ref('tab1');
const showEasilyModel = ref(false);
const showAutomateScoringModal = ref(false);

const goToNextGroup = (currentGroupId) => {
    const currentIndex = assessmentGroups.findIndex(g => g.id === currentGroupId);
    const nextGroup = assessmentGroups[currentIndex + 1];
    if (nextGroup) {
        activeTab.value = nextGroup.tab;
        showSidebar.value = false
        fetchAssessment();
    }
}

const goToPrevGroup = (currentGroupId) => {
    const currentIndex = assessmentGroups.findIndex(g => g.id === currentGroupId);
    const prevGroup = assessmentGroups[currentIndex - 1];
    if (prevGroup) {
        activeTab.value = prevGroup.tab;
        showSidebar.value = false;
        fetchAssessment();
    }
}

// Tab switch
function switchTab(group) {
    activeTab.value = group.tab;

    showSidebar.value = false
    if (group.id === 6) {
        fetchAssessment();
    }
}

const currentGroup = computed(() => assessmentGroups.find(g => g.tab === activeTab.value));

const fetchAssessment = async () => {
    try {
        loader.show();
        const { data } = await api.get(`/organizations/${organizationId.value}/assessments/${assessmentId.value}`);
        assessment.value = data.data;

        if (!assessment.value?.skip_group1 && !assessment.value?.group1 && !assessment.value?.skip_group2 && !assessment.value?.group2 && ['tab1', 'tab2'].includes(activeTab.value)) {
            showEasilyModel.value = true;
        } else {
            showEasilyModel.value = false;
        }

        Object.entries(assessment.value?.result?.flagged_groups || {}).forEach(([groupId, flagged]) => {
            flaggedCounts.value[groupId] = (flagged == 'Y') ? 1 : 0;
        });

        // The ceiling is five 0 scores IN A ROW, so count runs rather than the
        // group totals the API reports.
        const runs = zeroRunsByGroup(assessment.value?.result?.items || {});
        Object.entries(runs).forEach(([groupId, run]) => {
            zeroScoreCounts.value[groupId] = run;
        });

        organization.value = assessment.value?.learner?.organization;
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
}

const skipGroup = async () => {
    try {
        loader.show();
        await api.put(`/organizations/${organizationId.value}/assessments/${assessmentId.value}`, {
            skip_group1: 1,
            skip_group2: 1
        });

        assessment.value.skip_group1 = 1
        assessment.value.skip_group2 = 1

        showEasilyModel.value = false;
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
}

const learnerAssessments = computed(() => assessment.value?.learner?.assessments || []);

const handleAssessmentChange = (event) => {
    const selectedId = event.target.value;
    if (selectedId && selectedId !== assessmentId.value) {
        router.push({ name: 'LearnerAssessment', params: { organization: organizationId.value, learner: learnerId.value, assessment: selectedId } });
        showSidebar.value = false
    }
};

const goToLearnerDashboard = () => {
    router.push({ name: 'LearnerDashboard', params: { organization: organizationId.value, learner: learnerId.value } });
};

const exportAssessment = async () => {
    loader.show();
    try {
        const response = await api.post(
            `/organizations/${organizationId.value}/assessments/${assessmentId.value}/export`,
            {},
            { responseType: 'blob' }
        );
        // Create download link
        const url = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = `eesar_export_${new Date().toISOString().split('T')[0]}.json`;
        link.click();
        URL.revokeObjectURL(url);
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
};

watch(route, ({ params }) => {
    showEasilyModel.value = false;

    organizationId.value = Number(params.organization);
    learnerId.value = Number(params.learner);
    assessmentId.value = Number(params.assessment);
    fetchAssessment();
    activeTab.value = assessmentId.value != params.assessment ? 'tab1' : activeTab.value;
}, { deep: true });

onMounted(async () => {
    await fetchAssessment();
});

watch(zeroScoreCounts, (newVal, oldVal) => {
    if (newVal && oldVal) {
        // Only Groups 1-3 have a Decision Box, so only they have a ceiling.
        if (!CEILING_GROUPS.includes(currentGroup.value.id)) return;

        if (oldVal[currentGroup.value.id] && oldVal[currentGroup.value.id] < newVal[currentGroup.value.id] && newVal[currentGroup.value.id] >= CEILING_RUN_LENGTH) {
            showAutomateScoringModal.value = true;
        }
    }

}, { immediate: true, deep: true });

const canConductAssessment = computed(() => {
    return assessment.value?.can_edit;
});
</script>
